import { FC, useEffect, useTransition } from "react";
import { FormProvider } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import { PrivacyPDF, RASTER_LANDINGS, TermsPDF } from "@ryde/assets-landings";
import {
  CheckboxControlledMui,
  DropdownTemplate,
  MessagesDisplayContainer,
  PhoneTemplate,
  TextboxTemplate,
} from "@ryde/controls";
import { normalizePhoneNumber } from "@ryde/shared";
import {
  apiDataFetchingLandingAtom,
  LANDING_RYDE_SUPPORT_PHONE,
  landingMessagesAtom,
  useLandingProfile,
  WixChooseStationStepFormFieldsEnum,
} from "@ryde/utils-landings";

import { ContinueButton } from "../../widgets";
import { useStepChooseStationWix } from "./use-step-choose-station.wix";

export const StepChooseStationWix: FC = () => {
  const {
    methods,
    handleChooseStation,
    handleConsentToggle,
    isContinueCanBeApply,
    coursesDropdownList,
    stationsByCourseDropdownList,
    campusStationsDropdownList,
  } = useStepChooseStationWix();
  const { watch } = methods;

  const landingMessages = useRecoilValue(landingMessagesAtom);
  const { fetchAndSetProfile } = useLandingProfile();
  const [, startFetchProfileTransition] = useTransition();
  const isApiLoading = useRecoilValue(apiDataFetchingLandingAtom);
  const { t } = useTranslation();

  const acceptTermsWatchField = watch(
    WixChooseStationStepFormFieldsEnum.AcceptTerms,
  );
  const acceptPrivacyPolicyWatchField = watch(
    WixChooseStationStepFormFieldsEnum.AcceptPrivacyPolicy,
  );

  useEffect(() => {
    startFetchProfileTransition(() => {
      fetchAndSetProfile();
    });
  }, [fetchAndSetProfile]);

  return (
    <FormProvider {...methods}>
      <div className="landing-top-illustration">
        <img src={RASTER_LANDINGS.CarFestImagePNG} alt="Car fest" />
      </div>
      <div className="landing-title landing-title_choose-station">
        {t("landing.chooseStation.titleNvidiaShuttle")}
      </div>
      <div className="landing-sub-title">
        {t("landing.chooseStation.subTitleNvidiaShuttle")}
      </div>

      <div className="landing-form-field">
        <label htmlFor={WixChooseStationStepFormFieldsEnum.EmployeeNumber}>
          {t("landing.formLabel.employeeNumber")}*
        </label>
        <TextboxTemplate
          uniqueAttrId={WixChooseStationStepFormFieldsEnum.EmployeeNumber}
          name={WixChooseStationStepFormFieldsEnum.EmployeeNumber}
        />
      </div>

      <div className="landing-form-field">
        <label htmlFor={WixChooseStationStepFormFieldsEnum.FirstName}>
          {t("landing.formLabel.firstName")}*
        </label>
        <TextboxTemplate
          uniqueAttrId={WixChooseStationStepFormFieldsEnum.FirstName}
          name={WixChooseStationStepFormFieldsEnum.FirstName}
        />
      </div>

      <div className="landing-form-field">
        <label htmlFor={WixChooseStationStepFormFieldsEnum.LastName}>
          {t("landing.formLabel.lastName")}*
        </label>
        <TextboxTemplate
          uniqueAttrId={WixChooseStationStepFormFieldsEnum.LastName}
          name={WixChooseStationStepFormFieldsEnum.LastName}
        />
      </div>

      <div className="landing-form-field">
        <label htmlFor={WixChooseStationStepFormFieldsEnum.Email}>
          {t("landing.formLabel.email")}*
        </label>
        <TextboxTemplate
          uniqueAttrId={WixChooseStationStepFormFieldsEnum.Email}
          name={WixChooseStationStepFormFieldsEnum.Email}
        />
      </div>

      <div className="landing-form-field">
        <label htmlFor={WixChooseStationStepFormFieldsEnum.PhoneMobile}>
          {t("landing.formLabel.phone")}*
        </label>
        <PhoneTemplate
          uniqueAttrId={WixChooseStationStepFormFieldsEnum.PhoneMobile}
          name={WixChooseStationStepFormFieldsEnum.PhoneMobile}
          sx={{ size: "small" }}
          formAttributes={{ disablePhoneCallable: true, disabled: true }}
        />
      </div>

      <div className="landing-form-field">
        <label htmlFor={WixChooseStationStepFormFieldsEnum.SourceStation2Id}>
          {t("landing.formLabel.myFavoriteRoute")}*
        </label>
        <DropdownTemplate
          uniqueAttrId={WixChooseStationStepFormFieldsEnum.SourceStation2Id}
          name={WixChooseStationStepFormFieldsEnum.SourceStation2Id}
          size="medium"
          formAttributes={{
            selectItems: coursesDropdownList,
            skipDropdownItemsTranslation: true,
          }}
        />
      </div>

      <div className="landing-form-field">
        <label htmlFor={WixChooseStationStepFormFieldsEnum.DestinationId}>
          {t("landing.formLabel.myFavoriteStation")}*
        </label>
        <DropdownTemplate
          uniqueAttrId={WixChooseStationStepFormFieldsEnum.DestinationId}
          name={WixChooseStationStepFormFieldsEnum.DestinationId}
          size="medium"
          formAttributes={{
            selectItems: stationsByCourseDropdownList,
            skipDropdownItemsTranslation: true,
          }}
        />
      </div>

      <div className="landing-form-field">
        <label htmlFor={WixChooseStationStepFormFieldsEnum.CampusStationId}>
          {t("landing.formLabel.myOffice")}*
        </label>
        <DropdownTemplate
          uniqueAttrId={WixChooseStationStepFormFieldsEnum.CampusStationId}
          name={WixChooseStationStepFormFieldsEnum.CampusStationId}
          size="medium"
          formAttributes={{
            selectItems: campusStationsDropdownList,
            skipDropdownItemsTranslation: true,
          }}
        />
      </div>

      <div className="landing-form-field">
        <div className="landing-accept-terms">
          <CheckboxControlledMui
            width={16}
            height={16}
            checked={acceptTermsWatchField ?? false}
            setCheckedToggle={() =>
              handleConsentToggle(
                WixChooseStationStepFormFieldsEnum.AcceptTerms,
              )
            }
          />
          <span className="custom-label">
            {t("landing.formLabel.termsAndConditions")}{" "}
            <a
              href={TermsPDF}
              target="_blank"
              rel="noreferrer"
              aria-label={t("landing.formLabel.termsAndConditions")}
            >
              כאן
            </a>
          </span>
        </div>
      </div>

      <div className="landing-form-field">
        <div className="landing-accept-terms">
          <CheckboxControlledMui
            width={16}
            height={16}
            checked={acceptPrivacyPolicyWatchField ?? false}
            setCheckedToggle={() =>
              handleConsentToggle(
                WixChooseStationStepFormFieldsEnum.AcceptPrivacyPolicy,
              )
            }
          />
          <span className="custom-label">
            {t("landing.formLabel.acceptPrivacyPolicy")}{" "}
            <a
              href={PrivacyPDF}
              target="_blank"
              rel="noreferrer"
              aria-label={t("landing.formLabel.acceptPrivacyPolicy")}
            >
              כאן
            </a>
          </span>
        </div>
      </div>

      <MessagesDisplayContainer
        serverMessages={landingMessages?.messages}
        type={landingMessages?.type}
      />

      <ContinueButton
        disabled={!isContinueCanBeApply}
        onClick={handleChooseStation}
        loading={isApiLoading}
      />

      <div className="landing-container-footer">
        <div className="landing-container-footer-logo">
          <img
            className="landing-logo__img"
            src={RASTER_LANDINGS.RideLogoPNG}
            alt="Ryde logo"
          />
        </div>

        <span className="landing-customer-support custom-label">
          <Trans
            i18nKey="landing.helpDisclaimer"
            values={{
              phoneNumber: normalizePhoneNumber(LANDING_RYDE_SUPPORT_PHONE),
            }}
            components={{
              secure_link: (
                <a
                  aria-label="call center phone number"
                  href={`tel:${LANDING_RYDE_SUPPORT_PHONE}`}
                  target="_blank"
                  rel="noopener noreferrer"
                />
              ),
              br: <br />,
            }}
          />
        </span>
      </div>
    </FormProvider>
  );
};
